import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiEdit,
  FiTrash2,
  FiX,
  FiUsers,
  FiSearch,
  FiUserPlus,
  FiSave,
  FiShield,
  FiMail,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiChevronDown,
  FiCheck,
} from "react-icons/fi";
import { userApi } from "../../services/userApi";
import { roleApi } from "../../services/roleApi";
import { hasPermission } from "../../utils/permissions";

const getLoggedInUser = () => {
  try {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const normalizeRoleName = (value) =>
  String(value || "").trim().toLowerCase();

const isSuperAdminRole = (role) =>
  Boolean(role?.isSystem) &&
  normalizeRoleName(role?.name) === "super admin";

const isSuperAdminManagedUser = (user) =>
  Boolean(
    user?.role === "super_admin" &&
      normalizeRoleName(user?.roleName) === "super admin"
  );

const isSuperAdminUser = () => {
  const user = getLoggedInUser();

  return Boolean(
    user?.role === "super_admin" &&
      normalizeRoleName(user?.roleName) === "super admin" &&
      user?.permissions === "ALL"
  );
};

const isAssignedOnlyRole = (role) => {
  const roleName = String(role?.name || "").toLowerCase();

  return [
    "supplier",
    "partner",
    "forwarder",
    "freight",
    "external",
  ].some((keyword) => roleName.includes(keyword));
};

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-blue-600" : "bg-slate-300 dark:bg-slate-700"
      }`}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
}

function ContainedSelect({
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find((option) => String(option.value) === String(value)) ||
    options[0];

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="mt-2 w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((previous) => !previous)}
        className={`flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 py-2.5 text-left outline-none transition dark:bg-slate-950 ${
          open
            ? "border-blue-500 ring-4 ring-blue-50 dark:ring-blue-950"
            : "border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600"
        } disabled:cursor-not-allowed disabled:opacity-60`}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
            {selectedOption?.label || "Select an option"}
          </span>

          {selectedOption?.description ? (
            <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
              {selectedOption.description}
            </span>
          ) : null}
        </span>

        <FiChevronDown
          className={`shrink-0 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div
          className="mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-950"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const selected = String(option.value) === String(value);

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 dark:border-slate-800 ${
                  selected
                    ? "bg-blue-50 dark:bg-blue-950/60"
                    : "hover:bg-slate-50 dark:hover:bg-slate-900"
                }`}
              >
                <span className="min-w-0">
                  <span
                    className={`block text-sm font-semibold ${
                      selected
                        ? "text-blue-700 dark:text-blue-300"
                        : "text-slate-900 dark:text-white"
                    }`}
                  >
                    {option.label}
                  </span>

                  {option.description ? (
                    <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {option.description}
                    </span>
                  ) : null}
                </span>

                {selected ? (
                  <FiCheck className="mt-0.5 shrink-0 text-blue-600" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ModalShell({ title, subtitle, children, footer, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div>
            <h3 className="text-xl font-bold text-slate-950 dark:text-white">
              {title}
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FiX />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer ? (
          <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-800">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function UserEditor({ roles, user, onClose, onSaved }) {
  const isEdit = Boolean(user?.id);
  const superAdmin = isSuperAdminUser();
  const loggedInUser = getLoggedInUser();
  const editingSelf =
    isEdit && Number(user?.id) === Number(loggedInUser?.id);
  const editingProtectedSuperAdmin =
    isEdit && isSuperAdminManagedUser(user);
  const canSave =
    (superAdmin || hasPermission("users", isEdit ? "update" : "create")) &&
    (!editingProtectedSuperAdmin || superAdmin);

  const [saving, setSaving] = useState(false);
  const initialRoleId = user?.roleId || roles[0]?.id || "";
  const initialRole = roles.find(
    (role) => String(role.id) === String(initialRoleId)
  );

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    password: "",
    roleId: initialRoleId,
    accessScope:
      isSuperAdminRole(initialRole)
        ? "all"
        : user?.accessScope ||
          (isAssignedOnlyRole(initialRole) ? "assigned" : "all"),
    isActive: user?.isActive ?? true,
  });

  const selectedRole = roles.find(
    (role) => String(role.id) === String(form.roleId)
  );
  const selectedRoleIsSuperAdmin = isSuperAdminRole(selectedRole);

  const containerAccessOptions = [
    {
      value: "all",
      label: "All containers",
      description: "Internal user with access based on the selected role.",
    },
    {
      value: "assigned",
      label: "Assigned containers only",
      description: "Supplier or forwarder can access only assigned containers.",
    },
  ];

  const saveUser = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }

    if (!form.email.trim()) {
      alert("Email is required");
      return;
    }

    if (!isEdit && !form.password) {
      alert("Password is required for new user");
      return;
    }

    if (!form.roleId) {
      alert("Role is required");
      return;
    }

    if (!canSave) {
      alert("You do not have permission to save users.");
      return;
    }

    if (selectedRoleIsSuperAdmin && !superAdmin) {
      alert("Only a Super Admin can assign the Super Admin role.");
      return;
    }

    if (editingSelf && form.isActive === false) {
      alert("You cannot deactivate your own account.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        roleId: form.roleId,
        accessScope: selectedRoleIsSuperAdmin ? "all" : form.accessScope,
        isActive: Boolean(form.isActive),
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (isEdit) {
        await userApi.update(user.id, payload);
      } else {
        await userApi.create(payload);
      }

      await onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Edit User" : "Create User"}
      subtitle="Add users and assign the correct access role."
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="submit"
            form="user-editor-form"
            disabled={saving || !canSave}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
          >
            <FiSave />
            {saving ? "Saving..." : isEdit ? "Update User" : "Create User"}
          </button>
        </>
      }
    >
      <form id="user-editor-form" onSubmit={saveUser} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Full Name
            </label>
            <input
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Enter full name"
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Email Address
            </label>
            <input
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="user@example.com"
              type="email"
              disabled={isEdit}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800 dark:focus:ring-blue-950"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              {isEdit ? "New Password Optional" : "Password"}
            </label>
            <input
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
              placeholder={isEdit ? "Leave empty to keep old password" : "Enter password"}
              type="password"
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Role
            </label>
            <select
              value={form.roleId}
              disabled={editingProtectedSuperAdmin && !superAdmin}
              onChange={(event) => {
                const selectedRoleId = event.target.value;
                const selectedRole = roles.find(
                  (role) => String(role.id) === String(selectedRoleId)
                );

                setForm((previous) => ({
                  ...previous,
                  roleId: selectedRoleId,
                  accessScope: isSuperAdminRole(selectedRole)
                    ? "all"
                    : isAssignedOnlyRole(selectedRole)
                      ? "assigned"
                      : "all",
                }));
              }}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800 dark:focus:ring-blue-950"
            >
              <option value="">Select role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Phone Number
            </label>
            <input
              value={form.phone}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  phone: event.target.value,
                }))
              }
              placeholder="+44..."
              type="tel"
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Container Access
            </label>
            <ContainedSelect
              value={selectedRoleIsSuperAdmin ? "all" : form.accessScope}
              options={containerAccessOptions}
              disabled={selectedRoleIsSuperAdmin}
              ariaLabel="Container access"
              onChange={(accessScope) =>
                setForm((previous) => ({
                  ...previous,
                  accessScope,
                }))
              }
            />
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Assigned-only users can open only containers specifically assigned
              to them.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
          <Toggle
            checked={form.isActive}
            disabled={
              editingSelf || (editingProtectedSuperAdmin && !superAdmin)
            }
            onChange={(value) =>
              setForm((prev) => ({ ...prev, isActive: value }))
            }
          />

          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              Active user
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Disabled users cannot login.
            </p>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

export default function UserManagement() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editorUser, setEditorUser] = useState(null);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");

  const loggedInUser = getLoggedInUser();
  const superAdmin = isSuperAdminUser();

  const canCreateUser = superAdmin || hasPermission("users", "create");
  const canUpdateUser = superAdmin || hasPermission("users", "update");
  const canDeleteUser = superAdmin || hasPermission("users", "delete");

  const activeRoles = useMemo(
    () =>
      roles.filter(
        (role) =>
          role.isActive !== false &&
          (superAdmin || !isSuperAdminRole(role))
      ),
    [roles, superAdmin]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((user) =>
      [user.name, user.email, user.roleName, user.role]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, search]);

  const stats = useMemo(() => {
    const active = users.filter((user) => user.isActive !== false).length;
    const disabled = users.length - active;

    return {
      total: users.length,
      active,
      disabled,
      roles: roles.length,
    };
  }, [users, roles]);

  const loadData = async () => {
    setLoading(true);
    setMessage("");

    try {
      const [usersRes, rolesRes] = await Promise.all([
        userApi.list(),
        roleApi.list(),
      ]);

      setUsers(usersRes.data?.users || []);
      setRoles(rolesRes.data?.roles || []);
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const deleteUser = async (user) => {
    if (!canDeleteUser) {
      alert("You do not have permission to delete users.");
      return;
    }

    if (Number(user.id) === Number(loggedInUser?.id)) {
      alert("You cannot delete your own account.");
      return;
    }

    if (isSuperAdminManagedUser(user) && !superAdmin) {
      alert("Only a Super Admin can delete another Super Admin.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${user.name}"?`)) {
      return;
    }

    try {
      await userApi.remove(user.id);
      setMessage("User deleted successfully");
      await loadData();
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to delete user");
    }
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-100 px-6 py-5 dark:border-slate-800">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                <FiUsers />
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                  User Access Management
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Create users, assign roles, and control dashboard access.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            disabled={!canCreateUser}
            onClick={() => setEditorUser({})}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
          >
            <FiUserPlus />
            Add User
          </button>
        </div>
      </div>

      <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-800">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users by name, email, or role..."
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
          />
        </div>
      </div>

      {message ? (
        <div className="mx-6 mt-5 flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{message}</p>
        </div>
      ) : null}

      <div className="p-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-4">User</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Role</th>
                  <th className="px-5 py-4">Container Access</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-10 text-center">
                      <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                        <FiRefreshCw className="animate-spin" />
                        Loading users...
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-5 py-10 text-center text-slate-500"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800/70"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-bold text-white">
                            {(user.name || "U").slice(0, 1).toUpperCase()}
                          </div>

                          <div>
                            <p className="font-bold text-slate-950 dark:text-white">
                              {user.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              ID: {user.id}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <FiMail className="text-slate-400" />
                          {user.email}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                          <FiShield />
                          {user.accessRole?.name || user.roleName || user.role || "-"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            user.accessScope === "assigned"
                              ? "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {user.accessScope === "assigned"
                            ? "Assigned only"
                            : "All containers"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            user.isActive
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                              : "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
                          }`}
                        >
                          {user.isActive ? <FiCheckCircle /> : <FiAlertCircle />}
                          {user.isActive ? "Active" : "Disabled"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={
                              !canUpdateUser ||
                              (isSuperAdminManagedUser(user) && !superAdmin)
                            }
                            onClick={() => setEditorUser(user)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-blue-950"
                            title="Edit user"
                          >
                            <FiEdit />
                          </button>

                          <button
                            type="button"
                            disabled={
                              !canDeleteUser ||
                              Number(user.id) === Number(loggedInUser?.id) ||
                              (isSuperAdminManagedUser(user) && !superAdmin)
                            }
                            onClick={() => deleteUser(user)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 text-red-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                            title="Delete user"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editorUser ? (
        <UserEditor
          roles={activeRoles}
          user={editorUser.id ? editorUser : null}
          onClose={() => setEditorUser(null)}
          onSaved={loadData}
        />
      ) : null}
    </div>
  );
}